import clsx from 'clsx';
import { EqualIcon } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useFormattedDays } from '@/hooks/use-formatted-days';
import { durationInDays } from '@/utils/date-util';

interface BudgetCalculationProps {
  channelSlug: string;
  unitBudget: number | null;
  totalBudget: number | null;
  className?: string;
  isOpeningSection?: boolean;
  isPool?: boolean;
}

export function BudgetCalculationSection({
  channelSlug,
  unitBudget,
  totalBudget,
  className,
  isOpeningSection = false,
  isPool = false,
}: BudgetCalculationProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const formatDaysByBudgetType = useFormattedDays();
  const { getCalculationBudgetLabel } = useBudgetType();
  const {
    hookForm: { watch },
  } = useJobPostingContext();

  const budgetType = watch(`channels.${channelSlug}.budgetType`);
  const startsAt = watch(`channels.${channelSlug}.startsAt`);
  const endAt = watch(`channels.${channelSlug}.endAt`);
  const campaignDuration = useMemo(() => {
    if (!startsAt || !endAt) return null;
    return durationInDays(startsAt, endAt);
  }, [startsAt, endAt]);

  const displayBudget = useMemo(() => {
    if (isPool)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget for the pool',
      });

    if (!totalBudget) return '-';

    return formatCurrency(totalBudget);
  }, [formatCurrency, intl, isPool, totalBudget]);

  return (
    <div
      className={clsx('grid-4 grid w-full gap-4', className)}
      style={{
        gridTemplateColumns:
          'min-content auto min-content auto min-content auto',
      }}
    >
      {/* Display the calculation for non-lifetime budgets, show only total for lifetime budget */}
      {!(budgetType === 'lifetime' || !budgetType) && (
        <>
          <ReturnBackArrowIcon />

          {/* Input budget per budget type */}
          <div className="flex flex-col gap-1 whitespace-nowrap">
            <Typography variant={isOpeningSection ? 'caption' : 'subtitle2'}>
              {getCalculationBudgetLabel(budgetType)}
            </Typography>
            <Typography variant="body1">
              {unitBudget ? formatCurrency(unitBudget) : '-'}
            </Typography>
          </div>

          <CloseIcon className="text-fx-primary mt-0.5" />

          {/* Duration in days/weeks/months based on budget type */}
          <div className="flex flex-col gap-1 whitespace-nowrap">
            <Typography variant={isOpeningSection ? 'caption' : 'subtitle2'}>
              {intl.formatMessage({
                defaultMessage: 'Duration',
                description:
                  'Job posting budget math: how long the flight runs in days or weeks',
              })}
            </Typography>
            <Typography variant="body1">
              {campaignDuration
                ? formatDaysByBudgetType(
                    campaignDuration,
                    budgetType ?? undefined,
                  )
                : '-'}
            </Typography>
          </div>

          <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />
        </>
      )}

      {/* Calculated budget based on budget type and duration */}
      <div className="col-start-6 flex flex-col gap-1 whitespace-nowrap text-right">
        <Typography variant={isOpeningSection ? 'caption' : 'subtitle2'}>
          {isOpeningSection ? (
            <FormattedMessage
              defaultMessage="Spend"
              description="Budget summary: money spent or allocated for this posting"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Total spend"
              description="Budget summary header: full spend over the campaign flight"
            />
          )}
        </Typography>
        <Typography variant={isOpeningSection ? 'body1' : 'large2'}>
          {displayBudget}
        </Typography>
      </div>
    </div>
  );
}
